<template>
  <div v-if="schemes.length === 0 && schemesLoading" class="ml10em">
    {{ $t('message.LOADING') }}
  </div>
  <div v-else class="schemes-list">
    <div v-for="scheme in schemes" :key="scheme.id" class="schemes-list__item v-centerer">
      <div class="small-card-item v-centerer" @click="editScheme(scheme.id)">
        <EditableLabel v-model="scheme.name" disabled />
      </div>
      <div
        class="schemes-list__item__actions schemes-list__item__actions--teams action-btn"
        :title="$t('message.CONFIG_TEAMS_DISPLAY_TEAMS')"
        @click="showTeamsUsingScheme(scheme.id)"
      >
        <Icon> {{ teamMedium }} </Icon>
      </div>
      <div
        class="schemes-list__item__actions schemes-list__item__actions--delete action-btn"
        :title="$t('message.DELETE')"
        @click="deleteItem(scheme.id)"
      >
        <Icon> {{ deleteMedium }} </Icon>
      </div>
    </div>
    <div v-if="schemes.length == 0">
      {{ $t("message.CONFIG_NO_SCHEMES_FOUND") }}
    </div>
    <button
      v-if="!disabled"
      class="add-button blue mt10"
      @click="newScheme()"
    >
      <img src="/images/add-blue.png" alt="">
      {{ $t("message.CONFIG_CREATE_TEMPLATE") }}
    </button>
    <div v-if="schemeEditing" class="schemes-list__config-settings-wrapper">
      <CloseButton @click.native="closeEdit()" />
      <EditableLabel
        :value="schemeEditing.name"
        :legend="$t('message.CONFIG_SCHEME_NAME') + $t('message.TWO_DOTS')"
        class="schemes-list__config-settings-wrapper__edit-name"
        :placeholder="$t('message.CONFIG_SCHEME_NAME_PH')"
        @input="editSchemeField('name', $event)"
      />
      <IntCheckbox
        :value="schemeEditing.isDefault"
        class="ml10"
        :title="$t('message.CONFIG_SCHEME_DEFAULT')"
        @input="editSchemeField('isDefault', $event)"
      />
      <div class="field-hint ml10">
        {{ $t('message.CONFIG_SCHEME_DEFAULT_HINT') }}
      </div>
      <div
        class="schemes-list__config-settings-wrapper__btn-display-team action-btn"
        @click="showTeamsUsingScheme"
      >
        <Icon> {{ teamMedium }} </Icon>
        {{ $t('message.CONFIG_TEAMS_DISPLAY_TEAMS') }}
      </div>
      <ConfigSettingsIndicators
        ref="configIndic"
        class="f-h f-w schemes-list__config-settings-wrapper__config-settings"
        :admin="true"
        :scheme-id="schemeEditing.id"
        :themes="schemeEditing.themes"
        @update="fetchSchemes"
      />
    </div>
    <Modal
      class="schemes-list__modal"
      :value="modalIsOpen"
      large
      @input="modalIsOpen = $event"
    >
      <div class="schemes-list__modal-search">
        <Icon>{{ searchMedium }}</Icon>
        <input v-model="searchTeam" :placeholder="$t('message.FILTER_RADIO_ITEMS')">
      </div>
      <div v-if="teamsByTemplate" class="schemes-list__template-user-container">
        <div class="schemes-list__template-user-subcontainer">
          <div class="schemes-list__template-user-subcontainer__title">
            <span v-if="teamsUsingTemplate.length">{{ $t('message.CONFIG_TEAMS_USING_TEMPLATE') }}</span>
            <span v-else>{{ $t('message.CONFIG_TEAMS_NONE_USING_TEMPLATE') }}</span>
          </div>
          <div
            v-for="(template, key) in teamsUsingTemplate"
            :key="`using${key}`"
            class="schemes-list__template-user-subcontainer__template-user"
          >
            {{ $t('message.HYPHEN') }} {{ template }}
          </div>
        </div>
        <div v-if="teamsNotUsingTemplate.length" class="schemes-list__template-user-subcontainer">
          <div class="schemes-list__template-user-subcontainer__title">
            {{ $t('message.CONFIG_TEAMS_NOT_USING_TEMPLATE') }}
          </div>
          <div
            v-for="(template, key) in teamsNotUsingTemplate"
            :key="`notUsing${key}`"
            class="schemes-list__template-user-subcontainer__template-user"
          >
            {{ $t('message.HYPHEN') }} {{ template }}
          </div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
/**
 * Config schemes list to edit
 */
import * as toastr from 'toastr';
import { cloneDeep } from 'lodash';
import { mapState } from 'vuex';
import { FETCH_SCHEMES, CREATE_SCHEME, EDIT_SCHEME, DELETE_SCHEME } from '@/store/modules/config/action-types';
import { GET_SCHEMES } from '@/store/modules/config/mutation-types';
// Import components
import CloseButton from '@/components/close-button.vue';
import ConfigSettingsIndicators from '@/components/config-settings-indicators.vue';
import EditableLabel from '@/components/editable-label.vue';
import Icon from '@/components/icon.vue';
import IntCheckbox from '@/components/int-checkbox.vue';
import Modal from '@/components/modal.vue';
import { calert } from '@/utils/main';
// Import icons
import { deleteMedium, searchMedium, teamMedium } from '@/assets/icons';

export default {

  name: 'SchemesList',

  components: {
    CloseButton,
    ConfigSettingsIndicators,
    EditableLabel,
    Icon,
    IntCheckbox,
    Modal,
  },

  props: {
    disabled: Boolean,
    selectedScheme: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      deleteMedium,
      searchMedium,
      teamMedium,
      modalIsOpen: false,
      schemes: [],
      searchTeam: '',
      teamsByTemplate: null,
    };
  },

  computed: {
    /** Object: Scheme being edited */
    schemeEditing() {
      return this.schemes.find(s => s.id === this.selectedScheme);
    },
    /** Array: Names of teams that use the template that was clicked */
    teamsUsingTemplate() {
      const teams = this.teamsByTemplate.usingTemplate;
      const eSearch = this.searchTeam.sAccentless.toUpperCase().split(/[\s,-./']/);
      return teams.filter(name => !eSearch.some(term => !name.sAccentless.toUpperCase().includes(term)));
    },
    /** Array: Names of teams that don't use the template that was clicked */
    teamsNotUsingTemplate() {
      const teams = this.teamsByTemplate.notUsingTemplate;
      const eSearch = this.searchTeam.sAccentless.toUpperCase().split(/[\s,-./']/);
      return teams.filter(name => !eSearch.some(term => !name.sAccentless.toUpperCase().includes(term)));
    },
    ...mapState('config', {
      stateSchemes: state => state[GET_SCHEMES.stateKey] || [],
      schemesLoading: state => state[GET_SCHEMES.loadingKey],
    }),
  },

  watch: {
    stateSchemes() {
      this.schemes = cloneDeep(this.stateSchemes);
    },
  },

  created() {
    this.fetchSchemes();
  },

  methods: {
    /** Add a new scheme */
    newScheme() {
      this.$store.dispatch(`config/${CREATE_SCHEME}`);
    },
    /** Save into DB the updated scheme */
    editSchemeField(field, val) {
      const params = { id: this.schemeEditing.id };
      params[field] = val;
      this.$store.dispatch(`config/${EDIT_SCHEME}`, params);
    },
    /** Display edition screen */
    editScheme(schemeId) {
      const scheme = this.schemes.find(s => s.id === schemeId);
      if (scheme === undefined) return;
      this.$emit('schemeClick', scheme);
      if (!this.disabled) {
        this.$emit('select', schemeId);
      }
    },
    /** Close the edition screen */
    closeEdit() {
      this.$emit('select', 0);
      this.fetchSchemes();
    },
    /** Delete a scheme and related indicators */
    deleteItem(schemeId) {
      calert(this.$t('message.CONFIG_DELETE_SCHEME'), [
        {
          title: this.$t('message.DELETE'),
          action: () => {
            this.$store.dispatch(`config/${DELETE_SCHEME}`, { schemeId });
          },
        },
        { title: this.$t('message.CANCEL') },
      ]);
    },
    /** Fetch schemes */
    fetchSchemes() {
      if (this.currentTeam && this.currentTeam.id) {
        this.$store.dispatch(`config/${FETCH_SCHEMES}`);
      }
    },
    /** Fetch teams who's using the current scheme and open the teams modal */
    showTeamsUsingScheme(selectedSchemeId) {
      const schemeId = typeof selectedSchemeId === 'number' ? selectedSchemeId : this.selectedScheme;
      this.$http.post('/server/config/schemes/indicators/getTeamsUsingSchemes.php', { schemeId })
        .then(({ data }) => {
          if (data) {
            this.teamsByTemplate = data;
            this.modalIsOpen = true;
          } else {
            toastr.error(this.$t('message.CONNECTION_ERROR'));
          }
        });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.schemes-list {
  padding: 10px;
  box-sizing: border-box;
  position: relative;
  overflow: auto;

  .small-card-item {
    padding: 5px 7px;
    margin-right: 10px;
  }

  &__item {
    white-space: nowrap;

    &__actions {
      width: 1.8em;

      .icon {
        width: 1.4em;
        height: 1.4em;
        padding: 4px 4px;

        &:hover {
          cursor: pointer;
        }
      }

      img {
        width: 20px;
        height: auto;
        margin-left: 7px;
        cursor: pointer;
        box-sizing: border-box;
        padding: 2px;
      }

      &--teams {

        &:hover {
          background: #1c5bb9 !important;
          opacity: .7;
        }
      }

      &--delete {
        padding: 3px;

        &:hover {
          background: rgba(211, 74, 59, 0.8) !important;
          opacity: .8;
        }
      }
    }

    .action-btn {
      padding: 5px;
      margin-right: 10px;
      margin-top: -8px;
      background-color: white;
      box-shadow: 0px 1px 5px rgba(140, 138, 138, 0.5);

      .icon {
        width: 30px;
        margin-right: 0;
        color: grey;
      }

      &:hover {

        .icon {
          color: white;
        }
      }
    }
  }

  &__config-settings-wrapper {
    z-index: 10002;
    position: fixed;
    top: $header-height;
    left: $nav-width;
    width: calc(100% - #{$nav-width});
    height: calc(100% - #{$header-height});
    background: #ffffff;
    padding: 30px;
    box-sizing: border-box;
    overflow: auto;

    &__edit-name {
      margin: 0px 30px;
      font-size: 1.2em;
      padding: 0px 5px;
    }

    &__btn-display-team {
      margin: 1em 0 1em .8em;
      color: #58595B !important;
    }

    .action-btn {
      background-color: white;
      box-shadow: 0px 1px 5px rgba(140, 138, 138, 0.5);
      padding: 4px 6px 4px 0px;

      .icon {
        width: 30px;
        margin-right: 0;
        color: #58595B;
      }

      &:hover {
        color: white !important;

        .icon {
          color: white;
        }
      }
    }

    .content-wrapper {
      padding: 2em 4em;
    }
  }

  &__modal {

    .content-wrapper {
      height: calc(100% - 2em) !important;
      padding: 2em 4em 0em;
    }
  }

  &__modal-search {
    border: 1px solid #888888;
    height: 2em !important;
    width: 50%;
    margin-left: 25%;
    display: flex;
    align-items: center;

    .icon {
      margin: 0 0.5em;
      width: 1.4em;
      height: 1.4em;
    }

    input {
      padding: 0.25em;
      border: none;
      width: 100%;
    }
  }

  &__template-user-container {
    display: flex;
    justify-content: space-between;
    height: calc(100% - 4em) !important;
  }

  &__template-user-subcontainer {
    margin-top: 4em;
    width: 40%;

    &__title {
      font-size: 1.1em;
      margin-bottom: .5em;
    }

    &__template-user {
      position: relative;
      font-weight: 600;
      font-size: 1.1em;
      color: $color-blue-text;
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .schemes-list {
    &__config-settings-wrapper {
      top: $smartphone-header-height;
      left: $smartphone-nav-width;
      width: calc(100% - #{$smartphone-nav-width});
      height: calc(100% - #{$smartphone-header-height});
    }
  }
}
</style>
